import React from 'react';
import { Modal, Pressable, ScrollView, View } from 'react-native';
import { radius, useColors } from '../theme';
import { Icon } from '../icons/Icon';
import { T } from './Text';

/** A centred, scrollable dialog. Used by the menu editors. */
export function Sheet({
  visible,
  title,
  onClose,
  children,
  footer,
}: {
  visible: boolean;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
}) {
  const colors = useColors();
  if (!visible) return <Modal visible={false} transparent />;

  return (
    <Modal visible transparent animationType="fade" onRequestClose={onClose}>
      {/* The backdrop is an absolutely-positioned SIBLING of the card, not its
          parent. It used to wrap it, which put the scroll area inside two
          Pressables — and on Android a Pressable ancestor competes with the
          ScrollView for the touch responder, so drags on the content were
          swallowed and the sheet would not scroll at all. */}
      <View
        style={{
          flex: 1,
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
        }}
      >
        <Pressable
          onPress={onClose}
          accessibilityLabel={title}
          style={{
            position: 'absolute',
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: colors.scrim,
          }}
        />
        <View
          style={{
            width: '100%',
            maxWidth: 520,
            maxHeight: '92%',
            backgroundColor: colors.surface,
            borderWidth: 1,
            borderColor: colors.line,
            borderRadius: radius.lg,
            overflow: 'hidden',
          }}
        >
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              paddingVertical: 15,
              paddingHorizontal: 18,
              borderBottomWidth: 1,
              borderBottomColor: colors.line,
              backgroundColor: colors.surface2,
            }}
          >
            <T size={16} weight={600} serif style={{ flexShrink: 1 }}>
              {title}
            </T>
            <Pressable
              onPress={onClose}
              style={({ hovered }: any) => ({
                padding: 8,
                borderRadius: radius.md,
                borderWidth: 1,
                borderColor: hovered ? colors.gold : colors.line,
              })}
            >
              <Icon name="close" size={16} color={colors.textDim} />
            </Pressable>
          </View>

          {/* flexShrink lets the body give way to the header and footer inside
              the 92% cap. Without it the content keeps its full height, pushes
              the footer past the bottom edge, and leaves nothing to scroll. */}
          <ScrollView
            style={{ flexShrink: 1, minHeight: 0 }}
            contentContainerStyle={{ padding: 18, gap: 16 }}
            keyboardShouldPersistTaps="handled"
          >
            {children}
          </ScrollView>

          {footer ? (
            <View
              style={{
                flexDirection: 'row',
                gap: 10,
                padding: 16,
                borderTopWidth: 1,
                borderTopColor: colors.line,
                backgroundColor: colors.surface2,
              }}
            >
              {footer}
            </View>
          ) : null}
        </View>
      </View>
    </Modal>
  );
}

export function SheetButton({
  label,
  icon,
  onPress,
  tone = 'ghost',
}: {
  label: string;
  icon?: Parameters<typeof Icon>[0]['name'];
  onPress: () => void;
  tone?: 'ghost' | 'primary' | 'danger';
}) {
  const colors = useColors();
  const bg =
    tone === 'primary' ? colors.gold : tone === 'danger' ? 'transparent' : 'transparent';
  const fg =
    tone === 'primary' ? colors.onGold : tone === 'danger' ? colors.spice : colors.textDim;

  return (
    <Pressable
      onPress={onPress}
      style={({ pressed }) => ({
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        paddingVertical: 13,
        borderRadius: radius.md,
        borderWidth: 1,
        borderColor: tone === 'primary' ? colors.gold : tone === 'danger' ? colors.spice : colors.line,
        backgroundColor: bg,
        opacity: pressed ? 0.85 : 1,
      })}
    >
      {icon ? <Icon name={icon} size={16} color={fg} /> : null}
      <T size={13.5} weight={tone === 'primary' ? 800 : 700} color={fg}>
        {label}
      </T>
    </Pressable>
  );
}

/** Simple on/off row used for "Available". */
export function Toggle({
  label,
  value,
  onChange,
}: {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  const colors = useColors();
  return (
    <Pressable
      onPress={() => onChange(!value)}
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        paddingVertical: 12,
        paddingHorizontal: 14,
        borderRadius: radius.md,
        borderWidth: 1,
        borderColor: colors.line,
        backgroundColor: colors.surface2,
      }}
    >
      <T size={13.5} weight={600}>
        {label}
      </T>
      <View
        style={{
          width: 44,
          height: 26,
          borderRadius: radius.pill,
          backgroundColor: value ? colors.gold : colors.line,
          padding: 3,
          alignItems: value ? 'flex-end' : 'flex-start',
        }}
      >
        <View
          style={{
            width: 20,
            height: 20,
            borderRadius: 10,
            backgroundColor: value ? colors.onGold : colors.surface,
          }}
        />
      </View>
    </Pressable>
  );
}
